import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Mail, Eye, EyeOff, ArrowRight } from "lucide-react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AdminLogin() {
  const { user, ready, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();

  if (ready && user) {
    return <Navigate to={state?.from || "/admin/blogs"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Enter your email and password.");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      navigate(state?.from || "/admin/blogs", { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-[100svh] flex items-center justify-center px-6 py-24 relative overflow-hidden bg-bg-0">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(60rem_40rem_at_20%_-10%,rgba(138,43,226,0.28),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(50rem_30rem_at_100%_110%,rgba(0,240,255,0.18),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(40rem_30rem_at_50%_50%,rgba(255,0,127,0.10),transparent_60%)]" />
      </div>

      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        onSubmit={submit}
        className="w-full max-w-md glass-strong rounded-3xl p-8 sm:p-10 relative"
      >
        <div className="flex items-center gap-2.5 mb-8">
          <span className="relative w-9 h-9 rounded-lg overflow-hidden">
            <span className="absolute inset-0 bg-gradient-to-tr from-neon-magenta via-neon-orange to-neon-cyan" />
            <span className="absolute inset-[2px] rounded-md bg-bg-0 flex items-center justify-center font-mono font-extrabold text-[12px] text-white">NW</span>
          </span>
          <span className="font-semibold tracking-tight">Novarise <span className="text-white/50">Works</span></span>
        </div>

        <p className="overline mb-3">CMS Admin</p>
        <h1 className="text-3xl font-extrabold tracking-tight">Sign in</h1>
        <p className="mt-2 text-sm text-white/55">Manage your blog, drafts and images.</p>

        <div className="mt-8 space-y-4">
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/45">Email</span>
            <div className="mt-1.5 relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@novariseworks.com"
                autoComplete="email"
                className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition"
              />
            </div>
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/45">Password</span>
            <div className="mt-1.5 relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="admin-login-password"
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition"
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                aria-label={showPass ? "Hide password" : "Show password"}
              >
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>
        </div>

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={busy}
          className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-semibold bg-white text-bg-0 hover:shadow-[0_0_40px_rgba(0,240,255,0.35)] transition disabled:opacity-60"
        >
          {busy ? "Signing in…" : (<>Sign in <ArrowRight size={16} /></>)}
        </button>

        <p className="mt-6 text-[11px] font-mono tracking-[0.18em] uppercase text-white/35 text-center">
          Admin access only · Novarise Works
        </p>
      </motion.form>
    </div>
  );
}
